import React from 'react';
import { useFormikContext } from 'formik';
import { isEmpty, merge } from 'lodash';

import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import InnerCard from '~/components/core/Cards/InnerCard';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import PAYMENT_PROVIDERS from '~/server_shared/generated-types/PAYMENT_PROVIDERS';

import { PAYMENT_METHODS_DETAILS_DICT } from '../../../../../Types';
import { useSysconfig } from '../../../SystemConfigurationScreen';
import type { FundAccountSourceOrganization } from '../FundAccountSourceField';
import FundAccountSourceField, {
  doesProviderKeepFundAccount,
  getDefaultFundAccountConfig,
  isFundAccountSourceConfigurable,
} from '../FundAccountSourceField';
import PAYMENT_PROVIDERS_CONFIG from '../paymentProviders';
import SectionHeading from '../SectionHeading';
import type { FundAccountSourceConfig, PaymentMethodConfigurationValues, ProviderSpecificConfig } from '../types';

import { useStyles } from '../../../../../assets/styles';
import styles from '../PaymentsConfigurationForm.module.scss';

const PAYMENT_PROVIDER_FIELD_ID = 'method_configuration.payment_provider';
// MUI needs a concrete value for the "no provider" option; it is stored as null.
const NO_PAYMENT_PROVIDER_VALUE = '';

interface PaymentProviderSelectProps {
  paymentProvidersConfigured: string[];
  currentPaymentMethod: string;
  isCustomMethod: boolean;
  requiredFieldsForced: string[];
  setRequiredFieldsForced: (requiredFields: string[]) => void;
  readOnly?: boolean;
}

/** What this screen reads out of the form, on top of the shape `verificationConfig` declares. */
interface PaymentProviderSelectFormValues extends PaymentMethodConfigurationValues {
  /** The configuration row's sub-organization; null on the org-level row. */
  sub_organization_id?: number | null;
  method_configuration?: {
    payment_provider?: string | null;
    payment_provider_specific?: ProviderSpecificConfig | null;
    fund_account?: FundAccountSourceConfig | null;
    required_fields?: Record<string, boolean>;
    [key: string]: unknown;
  };
}

/**
 * Which payment provider routes a payment method, and — through the selected provider's descriptor —
 * whatever fields that provider needs. Provider-specific knowledge lives in `paymentProviders.ts`; this
 * component knows only that a provider may have some.
 */
function PaymentProviderSelect({
  paymentProvidersConfigured,
  currentPaymentMethod,
  isCustomMethod,
  requiredFieldsForced,
  setRequiredFieldsForced,
  readOnly,
}: PaymentProviderSelectProps) {
  const { isSubmitting, values, setFieldValue } = useFormikContext<PaymentProviderSelectFormValues>();
  const { classes } = useStyles();
  // `useSysconfig` comes from an untyped JS context (`createContext({})`), so TypeScript sees `{}`.
  // Naming the fields this component reads keeps the read typed without suppressing the error.
  const { organization } = useSysconfig() as { organization: FundAccountSourceOrganization };
  const selectedProvider = values.method_configuration?.payment_provider || NO_PAYMENT_PROVIDER_VALUE;
  // Provider -> the payment_provider_specific block it was last edited with, so switching providers and
  // back within one unsaved dialog session does not lose what was already there. A ref, not state:
  // nothing renders off it, and re-rendering on a stash would reset the field being edited.
  const providerSpecificByProvider = React.useRef<Record<string, ProviderSpecificConfig | null | undefined>>({});
  // The fund-account block gets the same treatment for the same reason. It is not part of any provider's
  // document — it is the generic key the `fund_accounts` module owns — but which provider routes the
  // method decides whether it applies, so it travels with the switch rather than with the document.
  const fundAccountByProvider = React.useRef<Record<string, FundAccountSourceConfig | null | undefined>>({});
  const isFundAccountSourceOffered = isFundAccountSourceConfigurable({
    organization,
    paymentProvider: selectedProvider,
  });

  // Method-intrinsic required fields declared in PAYMENT_METHODS_DETAILS_DICT (e.g. eCheck always needs
  // a recipient email because it is delivered by email). Forced on whichever provider is selected, and
  // with none — hence maintained here rather than in a provider's own fields component.
  const methodAlwaysRequiredFields = React.useMemo(() => {
    // Only some of the dict's methods declare the key, hence the `in` check rather than a lookup.
    const methodDetails =
      PAYMENT_METHODS_DETAILS_DICT[currentPaymentMethod as keyof typeof PAYMENT_METHODS_DETAILS_DICT];
    return methodDetails && 'always_required_fields' in methodDetails ? [...methodDetails.always_required_fields] : [];
  }, [currentPaymentMethod]);

  const providerForcedRequiredFields = React.useMemo(
    () =>
      PAYMENT_PROVIDERS_CONFIG[selectedProvider]?.getForcedRequiredFields?.({
        paymentMethodName: currentPaymentMethod,
        isCustomMethod,
      }) || [],
    [selectedProvider, currentPaymentMethod, isCustomMethod]
  );

  React.useEffect(() => {
    const forced = [...new Set([...providerForcedRequiredFields, ...methodAlwaysRequiredFields])];
    if ([...forced].sort().join(',') !== [...requiredFieldsForced].sort().join(',')) {
      setRequiredFieldsForced(forced);
    }
    // A method-intrinsic required field must also be checked (its value set), not just disabled.
    methodAlwaysRequiredFields.forEach((requiredFieldKey) => {
      if (!values.method_configuration?.required_fields?.[requiredFieldKey]) {
        setFieldValue(`method_configuration.required_fields.${requiredFieldKey}`, true);
      }
    });
  }, [
    providerForcedRequiredFields,
    methodAlwaysRequiredFields,
    requiredFieldsForced,
    setRequiredFieldsForced,
    setFieldValue,
    values.method_configuration?.required_fields,
  ]);

  const configuredProviders = Object.keys(PAYMENT_PROVIDERS).filter(
    (provider) =>
      paymentProvidersConfigured.includes(provider) &&
      PAYMENT_PROVIDERS_CONFIG[provider]?.supportedMethods.includes(currentPaymentMethod)
  );
  // The option list is what is configured *now*, but the selected value is what was *saved*. A method
  // saved with a provider that has since been unconfigured has no MenuItem to match, so MUI renders an
  // empty box (and warns about an out-of-range value) while that stale provider is still what gets
  // submitted. Keeping it in the list makes it visible and changeable; it drops out once switched away.
  const availableProviders =
    selectedProvider && !configuredProviders.includes(selectedProvider)
      ? [...configuredProviders, selectedProvider]
      : configuredProviders;

  const handleProviderChange = (newValue: string) => {
    const nextProvider = newValue || null;
    if (nextProvider === (values.method_configuration?.payment_provider ?? null)) {
      return;
    }

    // Every key under payment_provider_specific belongs to one provider, and the fields are only
    // *hidden* when another provider is selected, not cleared — so without this a vitesse -> stripe
    // switch would save the Vitesse account and programme ids under a Stripe-routed method.
    //
    // Stashed rather than discarded, and restored on the way back: an admin who opens the dropdown to
    // look at another provider and then reselects the first would otherwise be left with empty fields.
    providerSpecificByProvider.current[selectedProvider] = values.method_configuration?.payment_provider_specific;
    const stashed = providerSpecificByProvider.current[nextProvider || NO_PAYMENT_PROVIDER_VALUE];
    // The new provider's defaults have to land in the same batch as the provider itself: MUI freezes
    // whether an input is controlled at mount (see the InputBase note in core/Molecules/Fields/TextField),
    // so a value supplied after its select mounts never reaches the dropdown. A stashed value wins over
    // the default, exactly as a persisted one does when the form loads.
    const defaultProviderSpecific = nextProvider
      ? PAYMENT_PROVIDERS_CONFIG[nextProvider]?.getDefaultProviderSpecific?.({
          paymentMethodName: currentPaymentMethod,
          paymentProvider: nextProvider,
        })?.method_configuration?.payment_provider_specific || {}
      : {};
    setFieldValue('method_configuration.payment_provider_specific', merge({}, defaultProviderSpecific, stashed || {}));

    // The same stash, restore and seed for the fund-account block — and, when the next provider keeps no
    // fund account, a removal: the radio is only *hidden* by that gate, so a rules-sourced block would
    // otherwise be saved onto a method the new provider routes, where nothing ever reads it (the server
    // stores it, because it returns early for a provider whose account it does not resolve). `undefined`
    // removes the key rather than leaving a null behind, which is what Formik's `setIn` does with it.
    //
    // The removal reads the *provider* half of applicability alone, deliberately not the feature flag:
    // a stored block outlives the flag, so a flag-off organization round-tripping vitesse -> stripe ->
    // vitesse would otherwise have its persisted `{source: 'rules'}` silently dropped on the way out and
    // not restored on the way back. Seeding still goes through the full gate — `getDefaultFundAccountConfig`
    // is the same layer the initial values use — so no block is written where no radio renders.
    fundAccountByProvider.current[selectedProvider] = values.method_configuration?.fund_account;
    const stashedFundAccount = fundAccountByProvider.current[nextProvider || NO_PAYMENT_PROVIDER_VALUE];
    setFieldValue(
      'method_configuration.fund_account',
      doesProviderKeepFundAccount(nextProvider)
        ? stashedFundAccount ||
            getDefaultFundAccountConfig({ organization, paymentProvider: nextProvider }).method_configuration
              ?.fund_account
        : undefined
    );

    // Checked as well as locked: the effect above only sets the method-intrinsic ones, and a provider's
    // own forced fields are meaningless until it is the selected one.
    if (nextProvider) {
      (
        PAYMENT_PROVIDERS_CONFIG[nextProvider]?.getForcedRequiredFields?.({
          paymentMethodName: currentPaymentMethod,
          isCustomMethod,
        }) || []
      ).forEach((requiredFieldKey) => setFieldValue(`method_configuration.required_fields.${requiredFieldKey}`, true));
    }

    setFieldValue(PAYMENT_PROVIDER_FIELD_ID, nextProvider);
  };

  if (isEmpty(availableProviders)) {
    return null;
  }

  const ProviderFields = selectedProvider ? PAYMENT_PROVIDERS_CONFIG[selectedProvider]?.Fields : undefined;

  return (
    <>
      <Grid container spacing={1} className={styles.section}>
        <Grid item xs={6}>
          <SingleSelectField<string>
            id={PAYMENT_PROVIDER_FIELD_ID}
            label="Payment Provider"
            value={selectedProvider}
            onChange={handleProviderChange}
            disabled={readOnly || isSubmitting}
            // Choosing a provider is an explicit decision: an organization with a single configured
            // provider must not have it switched on merely by opening this dialog.
            autoSelectSingleOption={false}
            // Render the "None" option rather than a blank box when no provider is set. This replaces an
            // unambiguous on/off switch, so "no provider" has to stay as legible as it was.
            SelectProps={{ displayEmpty: true }}
            // Pinned, because `displayEmpty` and a floating label disagree about what "empty" means: the
            // stored value for "no provider" is '', so MUI judges the input unfilled and leaves the label
            // sitting in the box - on top of the "None" that `displayEmpty` renders there. The shared
            // TextField shrinks the label by itself for a filled value and offers InputLabelProps as the
            // override for the rest. Unconditional rather than derived: this select always displays
            // something, either a provider's name or "None", so there is no state where the label belongs
            // in the box as a placeholder.
            InputLabelProps={{ shrink: true }}
            className={classes.textField}
            fullWidth
          >
            <MenuItem value={NO_PAYMENT_PROVIDER_VALUE}>None</MenuItem>
            {availableProviders.map((provider) => (
              <MenuItem key={provider} value={provider}>
                {/* Falls back to the key so an unconfigured provider kept in the list is still named. */}
                {PAYMENT_PROVIDERS_CONFIG[provider]?.displayName || provider}
              </MenuItem>
            ))}
          </SingleSelectField>
        </Grid>
        {ProviderFields && (
          <ProviderFields
            currentPaymentMethod={currentPaymentMethod}
            isCustomMethod={isCustomMethod}
            readOnly={readOnly}
          />
        )}
      </Grid>
      {/* Its own full-width section below the provider's fields rather than a grid item beside them,
          so the group holds its place when the provider's account field unmounts (rules-sourced) —
          but still written by this generic layer, which is what owns the generic key. */}
      {isFundAccountSourceOffered && (
        <InnerCard className={styles.section}>
          <SectionHeading
            title="Fund account"
            subtitle="Select whether payments are funded from the provider's configured account or from an account resolved by the fund-account rules."
          />
          <FundAccountSourceField
            organizationId={organization.id}
            paymentMethod={currentPaymentMethod}
            subOrganizationId={values.sub_organization_id}
            readOnly={readOnly}
            disabled={isSubmitting}
          />
        </InnerCard>
      )}
    </>
  );
}

export default PaymentProviderSelect;
